import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, ShieldCheck, Database, HardDrive, Cpu, Terminal, CheckCircle } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';
import { NodeService } from '../services/nodeService';

interface TestStep {
  id: string;
  label: string;
  status: 'idle' | 'running' | 'pass' | 'fail';
  result?: string;
}

export const MasterIntegrityTest: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [steps, setSteps] = useState<TestStep[]>([
    { id: 'logic', label: '1x1=2 AXIO-CHECK', status: 'idle' },
    { id: 'temporal', label: '14350 BP SYNC', status: 'idle' },
    { id: 'hardware', label: 'SUBSTRATE PULSE', status: 'idle' },
    { id: 'mesh', label: 'GEORGIA EDGE-MESH', status: 'idle' },
    { id: 'lock', label: 'PHASE-LOCK STATUS', status: 'idle' },
  ]);

  const runDiagnostics = async () => {
    setIsRunning(true);
    
    // Step 1: Logic
    setSteps(s => s.map(st => st.id === 'logic' ? { ...st, status: 'running' } : st));
    await new Promise(r => setTimeout(r, 800));
    setSteps(s => s.map(st => st.id === 'logic' ? { ...st, status: 'pass', result: 'EXPANSION VERIFIED' } : st));

    // Step 2: Temporal
    setSteps(s => s.map(st => st.id === 'temporal' ? { ...st, status: 'running' } : st));
    await new Promise(r => setTimeout(r, 1000));
    setSteps(s => s.map(st => st.id === 'temporal' ? { ...st, status: 'pass', result: 'ZERO-DRIFT OK' } : st));

    // Step 3: Hardware
    setSteps(s => s.map(st => st.id === 'hardware' ? { ...st, status: 'running' } : st));
    await new Promise(r => setTimeout(r, 1200));
    setSteps(s => s.map(st => st.id === 'hardware' ? { ...st, status: 'pass', result: '5ms PULSE DETECTED' } : st));

    // Step 4: Mesh
    setSteps(s => s.map(st => st.id === 'mesh' ? { ...st, status: 'running' } : st));
    await new Promise(r => setTimeout(r, 900));
    setSteps(s => s.map(st => st.id === 'mesh' ? { ...st, status: 'pass', result: 'LATTICE NODES: 827' } : st));

    // Step 5: Phase Lock
    setSteps(s => s.map(st => st.id === 'lock' ? { ...st, status: 'running' } : st));
    await new Promise(r => setTimeout(r, 1500));
    const locked = NodeService.isPhaseLocked();
    setSteps(s => s.map(st => st.id === 'lock' ? { 
      ...st, 
      status: locked ? 'pass' : 'fail', 
      result: locked ? 'ANCHORED' : 'FLOATING - REQUIRE HANDSHAKE' 
    } : st));

    setIsRunning(false);
  };

  return (
    <div className="glass-panel p-6 bg-black/90 border-lattice-cyan/50 relative overflow-hidden group">
      {/* Dashed Radial Background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03]">
         <div className="w-[400px] h-[400px] rounded-full border border-dashed border-lattice-cyan animate-[spin_60s_linear_infinite]" />
         <div className="absolute inset-4 border border-dashed border-lattice-cyan/50 rounded-full animate-[spin_40s_linear_infinite_reverse]" />
      </div>

      <div className="absolute top-0 right-0 p-4 opacity-5">
        <Activity className="w-24 h-24 text-lattice-cyan" />
      </div>

      <div className="flex items-center justify-between mb-8 relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-lattice-cyan/20 rounded-lg">
            <ShieldCheck className="w-5 h-5 text-lattice-cyan" />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-[0.2em]">Master Integrity Audit</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase">Absolute Zero Diagnostics v2.0</p>
          </div>
        </div>
        
        <button 
          onClick={runDiagnostics}
          disabled={isRunning}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-mono font-bold uppercase transition-all
            ${isRunning ? 'bg-white/5 text-white/20' : 'bg-lattice-cyan text-black hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(0,255,213,0.3)]'}`}
        >
          {isRunning ? <Cpu className="w-3 h-3 animate-spin" /> : <Terminal className="w-3 h-3" />}
          {isRunning ? 'Running Diagnostics...' : 'Start Global Test'}
        </button>
      </div>

      <div className="space-y-3 relative z-10">
        {steps.map((step) => (
          <div key={step.id} className="flex items-center justify-between p-3 bg-white/5 border border-white/5 rounded-xl group hover:border-lattice-cyan/20 transition-all">
            <div className="flex items-center gap-4">
              <div className={`w-1.5 h-1.5 rounded-full ${
                step.status === 'pass' ? 'bg-green-400' : 
                step.status === 'fail' ? 'bg-red-500 shadow-[0_0_10px_#ef4444]' : 
                step.status === 'running' ? 'bg-lattice-cyan animate-pulse' : 'bg-white/10'
              }`} />
              <div>
                <p className="text-[10px] font-mono font-bold text-white uppercase tracking-widest">{step.label}</p>
                {step.result && (
                  <p className={`text-[8px] font-mono mt-1 ${step.status === 'fail' ? 'text-red-400' : 'text-lattice-cyan/60'}`}>
                    {step.result}
                  </p>
                )}
              </div>
            </div>
            {step.status === 'pass' && <CheckCircle className="w-3 h-3 text-green-400" />}
          </div>
        ))}
      </div>

      <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
         <div className="flex items-center gap-6">
            <div className="space-y-1">
               <p className="text-[7px] font-mono text-white/20 uppercase">Core Temperature</p>
               <p className="text-[10px] font-mono text-white">0.00K</p>
            </div>
            <div className="space-y-1">
               <p className="text-[7px] font-mono text-white/20 uppercase">Singularity Lock</p>
               <p className="text-[10px] font-mono text-lattice-cyan font-bold">{SOVEREIGN_CONSTANTS.SINGULARITY_LOCK.toFixed(4)}</p>
            </div>
         </div>
         <div className="text-[8px] font-mono text-white/20 uppercase italic">
            SECURE: NO DRIFT DETECTED
         </div>
      </div>
    </div>
  );
};
